/**
 * iter 73.10 — AnnouncementManagerDrawer
 *
 * Unified-drawer-architecture-compliant CRUD drawer for announcements.
 * Follows the same lifecycle primitives as LocationManagerDrawer /
 * UserManagerDrawer / DepartmentManagerDrawer (_managerShared.jsx):
 *   - useDrawerForm      → form/busy/err/dirty state + dirty-guard close
 *   - <DrawerFooter>     → sticky Cancel / Save + optional Delete slot
 *   - <ErrorBanner>      → error surface at top
 *
 * Fields:
 *   - Rich-ish content (plain textarea + hint about supported tags,
 *     since we don't have a WYSIWYG library installed and server-side
 *     bleach strips everything not in the allow-list).
 *   - Priority: info / warning / critical
 *   - Targeting: multi-selects for departments, roles, users
 *     (empty = target ALL internal members)
 *   - Optional starts_at / ends_at (datetime-local inputs)
 */
import { useState } from "react";
import { toast } from "sonner";
import { X, Info, Warning, WarningOctagon } from "@phosphor-icons/react";
import { api, formatErr } from "../../lib/api";
import { Drawer, Field } from "../UI";
import { ErrorBanner, DrawerFooter, useDrawerForm } from "./_managerShared";
import RichTextEditor from "../RichTextEditor";

// iter 73.10+ — visual parity with AnnouncementBanner so the preview
// matches the banner pixel-for-pixel. Kept in sync with the palette in
// components/AnnouncementBanner.jsx.
const PREVIEW_PALETTE = {
  info:     { wrap: "bg-blue-500/10 border-blue-500/30 text-blue-900 dark:text-blue-100",     Icon: Info },
  warning:  { wrap: "bg-amber-500/10 border-amber-500/40 text-amber-900 dark:text-amber-100", Icon: Warning },
  critical: { wrap: "bg-red-500/10 border-red-500/40 text-red-900 dark:text-red-100",         Icon: WarningOctagon },
};

const EMPTY = {
  content_html: "",
  priority: "info",
  targeting: { departments: [], roles: [], users: [] },
  starts_at: "",
  ends_at: "",
};

const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  // <input type="datetime-local"> wants `YYYY-MM-DDTHH:MM` in *local* time.
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const hydrate = (rec) => ({
  content_html: rec.content_html || "",
  priority: rec.priority || "info",
  targeting: {
    departments: rec.targeting?.departments || [],
    roles: rec.targeting?.roles || [],
    users: rec.targeting?.users || [],
  },
  starts_at: toDatetimeLocal(rec.starts_at),
  ends_at: toDatetimeLocal(rec.ends_at),
});

export default function AnnouncementManagerDrawer({
  open, onClose, onSuccess, mode = "create", initialData = null,
  orgId, departments = [], roles = [], users = [],
}) {
  const isEdit = mode === "edit";
  const f = useDrawerForm({ open, isEdit, initialData, emptyForm: EMPTY, hydrate });
  const { form, setForm, setField, busy, setBusy, err, setErr, requestClose } = f;

  const toggle = (axis, id) => {
    setForm((cur) => {
      const curList = cur.targeting[axis] || [];
      const next = curList.includes(id)
        ? curList.filter((x) => x !== id)
        : [...curList, id];
      return { ...cur, targeting: { ...cur.targeting, [axis]: next } };
    });
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        content_html: form.content_html.trim(),
        priority: form.priority,
        targeting: form.targeting,
        // datetime-local yields naive local strings — new Date() will parse
        // them in the user's local TZ, `.toISOString()` normalises to UTC.
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at:   form.ends_at   ? new Date(form.ends_at).toISOString()   : null,
      };
      let saved;
      if (isEdit) {
        const r = await api.patch(`/announcements/${initialData.id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post(`/announcements/org/${orgId}`, payload);
        saved = r.data;
      }
      toast.success(isEdit ? "Announcement updated." : "Announcement created.");
      onSuccess?.(saved);
      onClose?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!isEdit || !initialData) return;
    if (!window.confirm("Delete this announcement? It will disappear from every user's banner.")) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/announcements/${initialData.id}`);
      toast.success("Announcement deleted.");
      onSuccess?.(null);
      onClose?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const anyTargeting = (form.targeting.departments?.length ?? 0)
                     + (form.targeting.roles?.length ?? 0)
                     + (form.targeting.users?.length ?? 0);

  return (
    <Drawer
      open={open}
      onClose={() => requestClose(onClose)}
      title={isEdit ? "Edit announcement" : "New announcement"}
      size="narrow"
      testid="announcement-manager-drawer"
      footer={
        <DrawerFooter
          testid="announcement-manager-footer"
          onCancel={() => requestClose(onClose)}
          onSave={save}
          mode={mode}
          busy={busy}
          saveLabel={isEdit ? "Update announcement" : "Publish announcement"}
          saveDisabled={!form.content_html.replace(/<[^>]*>/g, "").trim()}
          left={isEdit && (
            <button
              type="button"
              className="btn btn-ghost text-destructive"
              onClick={remove}
              disabled={busy}
              data-testid="announcement-manager-delete-btn"
            >
              DELETE
            </button>
          )}
        />
      }
    >
      <ErrorBanner err={err} testid="announcement-manager-error" />

      <Field label="MESSAGE">
        <div data-testid="announcement-content-input">
          <RichTextEditor
            value={form.content_html}
            onChange={(html) => setField("content_html", html)}
            placeholder={'e.g. Scheduled maintenance Sat 10pm–11pm ET…'}
          />
        </div>
        <div className="text-[10px] text-muted-foreground mt-1">
          Bold, italic, strike, links, and lists supported. Anything else is stripped server-side.
        </div>
      </Field>

      <Field label="PRIORITY">
        <div className="flex gap-2" data-testid="announcement-priority-group">
          {["info", "warning", "critical"].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setField("priority", p)}
              className={`btn btn-sm flex-1 ${form.priority === p ? "btn-primary" : "btn-ghost"}`}
              data-testid={`announcement-priority-${p}`}
            >
              {p.toUpperCase()}
            </button>
          ))}
        </div>
      </Field>

      {/* iter 73.10+ — Live preview mirroring AnnouncementBanner's DOM. */}
      <Field label="LIVE PREVIEW">
        {(() => {
          const p = PREVIEW_PALETTE[form.priority] || PREVIEW_PALETTE.info;
          const Icon = p.Icon;
          const hasContent = form.content_html.replace(/<[^>]*>/g, "").trim().length > 0;
          return (
            <div
              className={`flex items-start gap-3 px-3.5 py-2.5 rounded border ${p.wrap}`}
              data-testid="announcement-live-preview"
            >
              <Icon size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
              {hasContent ? (
                <div
                  className="text-sm leading-snug flex-1 min-w-0 [&_a]:underline [&_a:hover]:opacity-80 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                  dangerouslySetInnerHTML={{ __html: form.content_html }}
                />
              ) : (
                <div className="text-sm leading-snug flex-1 min-w-0 italic opacity-60">
                  Your announcement will appear here…
                </div>
              )}
              <span
                className="flex-shrink-0 opacity-70 mt-0.5"
                title="Dismiss button (users see this on every banner — even critical)"
              >
                <X size={13} />
              </span>
            </div>
          );
        })()}
        <div className="text-[10px] text-muted-foreground mt-1">
          Exact rendering of what every targeted user will see, including the dismiss button.
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="STARTS AT (OPTIONAL)">
          <input
            type="datetime-local"
            className="input"
            value={form.starts_at}
            onChange={(e) => setField("starts_at", e.target.value)}
            data-testid="announcement-starts-at"
          />
        </Field>
        <Field label="ENDS AT (OPTIONAL)">
          <input
            type="datetime-local"
            className="input"
            value={form.ends_at}
            onChange={(e) => setField("ends_at", e.target.value)}
            data-testid="announcement-ends-at"
          />
        </Field>
      </div>

      {/* iter 73.10+ — Auto-expire preset chips. */}
      <div className="mb-3 -mt-1" data-testid="announcement-expire-presets">
        <div className="text-[10px] text-muted-foreground mb-1.5">
          QUICK EXPIRE:
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { label: "1 hour",  ms: 60 * 60 * 1000 },
            { label: "1 day",   ms: 24 * 60 * 60 * 1000 },
            { label: "1 week",  ms: 7 * 24 * 60 * 60 * 1000 },
            { label: "1 month", ms: 30 * 24 * 60 * 60 * 1000 },
          ].map((p) => (
            <button
              key={p.label}
              type="button"
              className="px-2 py-0.5 rounded text-[11px] border border-border hover:bg-muted"
              onClick={() => {
                const now = new Date();
                const end = new Date(now.getTime() + p.ms);
                const pad = (n) => String(n).padStart(2, "0");
                const local = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`;
                setField("ends_at", local);
              }}
              data-testid={`announcement-expire-preset-${p.label.replace(/\s+/g, "-")}`}
            >+{p.label}</button>
          ))}
          {form.ends_at && (
            <button
              type="button"
              className="px-2 py-0.5 rounded text-[11px] border border-border hover:bg-muted text-muted-foreground"
              onClick={() => setField("ends_at", "")}
              data-testid="announcement-expire-preset-clear"
            >Clear</button>
          )}
        </div>
      </div>

      <Field label="TARGETING">
        <div className="text-[10px] text-muted-foreground mb-2">
          Leave everything empty to target ALL internal members. Otherwise a user is shown the banner when they match ANY selected department, role, or specific user.
          {anyTargeting === 0 && <span className="ml-1 text-amber-600 font-semibold">(currently: everyone)</span>}
        </div>

        {departments.length > 0 && (
          <div className="mb-2">
            <div className="label-xs mb-1">DEPARTMENTS ({form.targeting.departments.length}/{departments.length})</div>
            <div className="flex flex-wrap gap-1.5" data-testid="announcement-target-departments">
              {departments.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggle("departments", d.id)}
                  className={`px-2 py-0.5 rounded text-[11px] border ${form.targeting.departments.includes(d.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                  data-testid={`announcement-target-dept-${d.id}`}
                >{d.name}</button>
              ))}
            </div>
          </div>
        )}

        {roles.length > 0 && (
          <div className="mb-2">
            <div className="label-xs mb-1">ROLES ({form.targeting.roles.length}/{roles.length})</div>
            <div className="flex flex-wrap gap-1.5" data-testid="announcement-target-roles">
              {roles.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => toggle("roles", r.id)}
                  className={`px-2 py-0.5 rounded text-[11px] border ${form.targeting.roles.includes(r.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                  data-testid={`announcement-target-role-${r.id}`}
                >{r.name}</button>
              ))}
            </div>
          </div>
        )}

        {users.length > 0 && (
          <div>
            <div className="label-xs mb-1">SPECIFIC USERS ({form.targeting.users.length}/{users.length})</div>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-auto" data-testid="announcement-target-users">
              {users.map((usr) => (
                <button
                  key={usr.id}
                  type="button"
                  onClick={() => toggle("users", usr.id)}
                  className={`px-2 py-0.5 rounded text-[11px] border ${form.targeting.users.includes(usr.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                  data-testid={`announcement-target-user-${usr.id}`}
                >{usr.name || usr.email}</button>
              ))}
            </div>
          </div>
        )}
      </Field>
    </Drawer>
  );
}
